import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ConfirmationDialogResult, EnumHelper, RecordEducationScopeUnit } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import { SaveDataObj } from "source/framework/saveData";
import { IRecordFurtherEducationEditItem } from "source/models/recordFurtherEducation/recordFurtherEducationEditItem";
import * as recordService from "source/models/services/recordService";
import * as storeActions from "source/models/store/storeActions";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormSelect } from "source/components/framework/FormSelect";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IRecordFurtherEducationDialogProp {
    classes?: string;
    id: string;
    companyId: string;
    groupId: string;
    canBeRemoved: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: (refreshTree: boolean) => void;
    onRemove: (id: string) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IRecordFurtherEducationDialogState {
    groups: IIdTitle[];
    groupId: string;
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    scopeUnit: number;
    scope: number;
    validity: number;
    item: IRecordFurtherEducationEditItem;
}

export class RecordFurtherEducationDialog extends React.Component<IRecordFurtherEducationDialogProp, IRecordFurtherEducationDialogState> {
    private orgStateHash: string = "";

    constructor(props: IRecordFurtherEducationDialogProp) {
        super(props);
        this.state = {
            groups: [],
            item: null,
            groupId: props.groupId,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            scopeUnit: 99,
            scope: 0,
            validity: 0,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => recordService.getRecordFurtherEducationEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.education;
        const state = {
            ...this.state,
            groups: result.furtherEducationGroups,
            item,
            groupId: item.isNew() ? props.groupId : item.groupId,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            scopeUnit: item.scopeUnit,
            scope: item.scope,
            validity: item.validity
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IRecordFurtherEducationDialogProp, prevState: IRecordFurtherEducationDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "scope") {
            this.setState({ scope: value.toInteger() });
        }
    };

    getSaveDataFromState = (props: IRecordFurtherEducationDialogProp, state: IRecordFurtherEducationDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", state.id);
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("groupId", state.groupId);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("scopeUnit", state.scopeUnit ?? null);
        result.append("scope", state.scope ?? null);
        result.append("validity", state.validity ?? null);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => recordService.saveRecordFurtherEducationEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = this.getSaveDataFromState(props, { ...state, ...stateChange }).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordFurtherEducationParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove(state.id);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Dialog
                id="record-further-education-dialog"
                maxWidth="sm"
                title={state.isNew
                    ? ((props.companyId ? Translations.AddFurtherEducation : Translations.AddDefaultFurtherEducation) + " / " + Translations.Competency)
                    : ((props.companyId ? Translations.FurtherEducation : Translations.DefaultFurtherEducation) + " / " + Translations.Competency + ": " + state.item.code)}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Name} required margin={0}>
                                    <TextField type="text" name="code" title={Translations.Name} inputProps={{ maxLength: 100 }} disabled={false} value={state.code} required={true} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Group}>
                                    <FormSelect
                                        value={state.groupId}
                                        tooltip={Translations.Group}
                                        items={state.groups.map(group => ({ value: group.id, title: group.title }))}
                                        onChange={(groupId) => this.setState({ groupId })}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.ScopeUnit}>
                                    <FormSelect
                                        value={state.scopeUnit.toString(10)}
                                        tooltip={Translations.ScopeUnit}
                                        items={[
                                            { value: "99", title: "'" + Translations.NotSelected + "'" },
                                            { value: RecordEducationScopeUnit.Hour.toString(10), title: EnumHelper.RecordEducationScopeUnitToStr(RecordEducationScopeUnit.Hour) },
                                            { value: RecordEducationScopeUnit.Day.toString(10), title: EnumHelper.RecordEducationScopeUnitToStr(RecordEducationScopeUnit.Day) },
                                            { value: RecordEducationScopeUnit.CourseCredit.toString(10), title: EnumHelper.RecordEducationScopeUnitToStr(RecordEducationScopeUnit.CourseCredit) },
                                            { value: RecordEducationScopeUnit.StudyWeek.toString(10), title: EnumHelper.RecordEducationScopeUnitToStr(RecordEducationScopeUnit.StudyWeek) },
                                            { value: RecordEducationScopeUnit.Course.toString(10), title: EnumHelper.RecordEducationScopeUnitToStr(RecordEducationScopeUnit.Course) }
                                        ]}
                                        onChange={(scopeUnit) => this.setState({ scopeUnit: scopeUnit.toInteger() })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={`${Translations.Scope} (0 = ${Translations.ScopeCanBeChanged.toLocaleLowerCase()})`}>
                                    <TextField
                                        name="scope"
                                        title={Translations.Scope}
                                        type="number"
                                        inputProps={{ maxLength: 5 }}
                                        value={state.scope}
                                        onChange={this.handleChange}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            inputProps: { min: 0, max: 100 },
                                            endAdornment: state.scopeUnit < 99
                                                ? <Box component="span" fontSize="0.75rem" paddingLeft="10px" color="text.secondary">
                                                    {state.scope === 1
                                                        ? EnumHelper.RecordEducationScopeUnitToStr(state.scopeUnit)
                                                        : EnumHelper.RecordEducationScopeUnitToStrPlural(state.scopeUnit)
                                                    }
                                                </Box>
                                                : null
                                        }} />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.Validity}>
                                    <FormSelect
                                        value={state.validity.toString(10)}
                                        tooltip={Translations.Validity}
                                        items={[
                                            { value: "0", title: "'" + Translations.NotSelected + "'" },
                                            { value: "1", title: Translations.Year },
                                        ].concat([2, 3, 4, 5].map(i => ({ value: i.toString(10), title: `${i} ${Translations.Years}` })))}
                                        onChange={(validity) => this.setState({ validity: validity.toInteger() })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Description}>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} multiline minRows={5} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!state.isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={state.isNew}
                            onCancel={this.handleCancel}
                            onRemove={!state.isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}